import { useEffect, useMemo, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CopyButton from "@/components/affiliate/CopyButton";
import FilterChips from "@/components/affiliate/FilterChips";
import useProgramFromQuery from "@/lib/useProgramFromQuery";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Sample posts — full ready-to-adapt templates. Each one shows in a
// preformatted view with character count and a one-tap copy button.
//
// API contract:
//   GET /affiliate/content/posts →
//     { posts: [{id, title, body, programs:[], channel?, disclaimerId?}] }
export default function AssetsPosts() {
  const [posts, setPosts] = useState(null);
  const [program, setProgram] = useProgramFromQuery();
  const [channel, setChannel] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/sample-posts", { auth: true });
        const items = res?.items || res?.posts || [];
        const normalized = items.map((p) => ({
          id: p.id || p._id || p.contentId,
          title: p.title || "Sample post",
          // BE bakes the disclaimer into `body` already — we render it
          // as-is, no appending on the FE side.
          body: p.body || p.text || "",
          programs: p.programs || (p.programSlug ? [p.programSlug] : []),
          channel: p.channel || p.metadata?.channel,
        })).filter((p) => p.body);
        if (!cancelled) setPosts(normalized);
      } catch {
        if (!cancelled) setPosts([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => {
    if (!posts) return [];
    const set = new Set();
    posts.forEach((p) => (p.programs || []).forEach((x) => set.add(x)));
    return Array.from(set);
  }, [posts]);
  const channels = useMemo(() => {
    if (!posts) return [];
    return Array.from(new Set(posts.map((p) => p.channel).filter(Boolean)));
  }, [posts]);

  const visible = useMemo(() => {
    if (!posts) return [];
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (program !== "all" && !(p.programs || []).includes(program)) return false;
      if (channel !== "all" && p.channel !== channel) return false;
      if (q && !(`${p.title || ""} ${p.body || ""}`).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [posts, program, channel, query]);

  if (posts === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="SAMPLE" italic="posts." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Full post templates with the right disclaimers baked in. Copy, adapt the personal details, post.
      </p>
      <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search posts…"
          data-testid="posts-search"
          style={{ padding: "12px 14px", fontSize: 14, background: C.pure, border: `1px solid ${C.warmGray3}`, outline: "none" }}
        />
        {programs.length > 1 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray, marginBottom: 8 }}>PROGRAM</div>
            <FilterChips value={program} onChange={setProgram} options={programs} testIdPrefix="posts-program" />
          </div>
        )}
        {channels.length > 1 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray, marginBottom: 8 }}>CHANNEL</div>
            <FilterChips value={channel} onChange={setChannel} options={channels} testIdPrefix="posts-channel" />
          </div>
        )}
      </div>
      <div data-testid="posts-list" style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 18 }}>
        {visible.length === 0 ? (
          <div data-testid="posts-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No sample posts match these filters.
          </div>
        ) : (
          visible.map((p) => <PostCard key={p.id} post={p} />)
        )}
      </div>
    </div>
  );
}

function PostCard({ post }) {
  const len = (post.body || "").length;
  return (
    <article
      data-testid={`post-${post.id}`}
      style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 22 }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
        <div>
          {post.channel && (
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
              ★ {String(post.channel).toUpperCase()}
            </div>
          )}
          <h3 style={{ margin: "4px 0 0", fontSize: 19, fontWeight: 800, letterSpacing: "-0.01em" }}>{post.title || "Sample post"}</h3>
        </div>
        <div style={{ fontSize: 11, color: C.warmGray, fontWeight: 700, letterSpacing: "0.06em" }}>
          {len} CHARS
          {post.channel === "twitter" && len > 280 && (
            <span style={{ color: C.coral, marginLeft: 8 }}>· OVER 280</span>
          )}
        </div>
      </header>
      <pre
        style={{
          margin: "14px 0 0",
          padding: 18,
          background: C.bone,
          color: C.ink,
          fontSize: 14,
          lineHeight: 1.7,
          fontFamily: '"Instrument Serif", Georgia, serif',
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {post.body}
      </pre>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
        <CopyButton text={post.body} testId={`post-copy-${post.id}`} label="COPY POST" />
      </div>
    </article>
  );
}
